import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown } from 'lucide-react';
import { type LogLine } from '@aiai/shared';
import { LiveDot } from './Badge';
import { ConfirmDialog } from './ConfirmDialog';
import { Spinner } from './Spinner';
import { titleCase } from '../lib/format';
import { buildTimeline, type TimelineItem, type TimelineLine, type ToolCall } from './timeline';

const STREAM_COLOR: Record<LogLine['stream'], string> = {
  stdout: 'text-ink-muted',
  stderr: 'text-bad',
  system: 'text-signal',
};

const ROLE_COLOR: Record<string, string> = {
  worker: 'text-warning',
  // Legacy roles from historical run data — kept for display only.
  autonomous: 'text-info',
  reporter: 'text-ink-muted',
  researcher: 'text-ok',
};

function ts(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '--:--:--';
  return d.toLocaleTimeString(undefined, { hour12: false });
}

function tsHHMM(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '--:--';
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
}

// ─── prose inline-code rendering ────────────────────────────────────────────

// Splits on backtick parity rather than a paired regex: every odd-indexed
// segment is code, every even-indexed one is plain text. A stray/unmatched
// backtick makes the total backtick count odd, which yields an even-length
// split — in that case the trailing segment is folded back into plain text
// so it never renders as a false/empty code span (fail-open).
function renderInlineCode(text: string, keyPrefix: string) {
  const segments = text.split('`');
  if (segments.length % 2 === 0) {
    const last = segments.pop();
    if (segments.length > 0) segments[segments.length - 1] += '`' + last;
    else segments.push('`' + last);
  }
  return segments.map((segment, i) =>
    i % 2 === 1 ? (
      <code key={`${keyPrefix}-${i}`} className="rounded bg-surface-2 px-1 font-mono text-xs text-ink">
        {segment}
      </code>
    ) : (
      <span key={`${keyPrefix}-${i}`}>{segment}</span>
    ),
  );
}

// Splits prose on fenced (```-delimited) code blocks, rendering fence
// contents in a styled monospace block (language tag line dropped) and
// running everything else through the inline-code splitter. An unterminated
// fence has no closing ``` to match, so the regex simply doesn't match it —
// the whole text falls through to renderInlineCode as plain prose (fail-open).
function renderProseText(text: string) {
  const fenceRe = /```[a-zA-Z]*\n?([\s\S]*?)```/g;
  const nodes: JSX.Element[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = fenceRe.exec(text)) !== null) {
    if (match.index > last) {
      nodes.push(<span key={`t-${i}`}>{renderInlineCode(text.slice(last, match.index), `t-${i}`)}</span>);
    }
    nodes.push(
      <div
        key={`f-${i}`}
        className="my-1 rounded bg-surface-2 p-2 font-mono text-xs text-ink-muted overflow-x-auto whitespace-pre"
      >
        {match[1]}
      </div>,
    );
    last = fenceRe.lastIndex;
    i++;
  }
  if (last < text.length) {
    nodes.push(<span key={`t-${i}`}>{renderInlineCode(text.slice(last), `t-${i}`)}</span>);
  }
  return nodes;
}

// ─── raw row rendering (copied from LogConsole) ─────────────────────────────

function renderRawRow(line: TimelineLine) {
  return (
    <div key={line.seq} className="flex gap-2 whitespace-pre-wrap break-words">
      <span className="shrink-0 select-none text-ink-faint">{ts(line.ts)}</span>
      {line.role ? (
        <span className={`shrink-0 select-none ${ROLE_COLOR[line.role] ?? 'text-ink-faint'}`}>
          {titleCase(line.role).slice(0, 4).toLowerCase()}
        </span>
      ) : (
        <span className="shrink-0 select-none text-ink-faint">····</span>
      )}
      <span className={`min-w-0 ${STREAM_COLOR[line.stream]}`}>{line.text}</span>
    </div>
  );
}

// ─── tools group ─────────────────────────────────────────────────────────────

function ToolRow({ tool }: { tool: ToolCall }) {
  return (
    <div className="py-0.5">
      <span className="inline-flex items-center gap-1.5 rounded-[4px] border border-line bg-surface-1 px-2 py-0.5 font-mono text-2xs">
        {tool.name && <span className="font-bold text-signal">{tool.name}</span>}
        {tool.detail && (
          <span className="max-w-[40ch] truncate text-ink-muted">{tool.detail}</span>
        )}
      </span>
      {tool.result !== undefined && (
        <div className={`mt-0.5 pl-2 font-mono text-2xs ${tool.isError ? 'text-bad' : 'text-ink-faint'}`}>
          ↳ {tool.result}
        </div>
      )}
    </div>
  );
}

function ToolsGroup({
  item,
  expanded,
  onToggle,
}: {
  item: Extract<TimelineItem, { kind: 'tools' }>;
  expanded: boolean;
  onToggle: () => void;
}) {
  const count = item.tools.length;
  return (
    // Tool-group expanders render dynamic action counts as button text — skip tracking
    <div data-no-analytics className="my-1">
      <button
        type="button"
        onClick={onToggle}
        className="block w-full text-left font-mono text-xs text-ink-faint hover:text-ink-muted cursor-pointer"
      >
        {expanded ? '▾' : '▸'} {count} action{count !== 1 ? 's' : ''} — {item.summary}
      </button>
      {expanded && (
        <div className="border-l border-line ml-1 pl-3">
          {item.tools.map((tool) => (
            <ToolRow key={tool.key} tool={tool} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── timeline item rendering ─────────────────────────────────────────────────

/** Inner content for each timeline item (no key — key goes on the wrapper). */
function renderItemContent(
  item: TimelineItem,
  expanded: Set<string>,
  toggleGroup: (key: string) => void,
): JSX.Element | null {
  switch (item.kind) {
    case 'prose':
      return (
        <div className="my-0.5 font-sans max-w-[68ch] text-ink whitespace-pre-wrap">
          {renderProseText(item.text)}
        </div>
      );
    case 'thinking':
      return (
        <div className="my-0.5 italic text-ink-faint border-l-2 border-line pl-3 whitespace-pre-wrap">
          {item.text}
        </div>
      );
    case 'stage':
      return (
        <div className="flex items-center gap-2 mt-2 mb-0.5">
          <span className="shrink-0 font-display text-[0.625rem] font-bold uppercase tracking-[0.12em] text-ink-muted">
            {item.text}
          </span>
          <div className="h-px flex-1 bg-line" />
        </div>
      );
    case 'gate':
      return (
        <div
          className={`my-0.5 font-display text-[0.625rem] font-bold uppercase tracking-[0.1em] ${
            item.isError ? 'text-bad' : 'text-ok'
          }`}
        >
          {item.text}
        </div>
      );
    case 'system':
      return (
        <div className="font-mono text-xs text-ink-faint whitespace-pre-wrap">
          {item.text}
        </div>
      );
    case 'tools':
      return (
        <ToolsGroup
          item={item}
          expanded={expanded.has(item.key)}
          onToggle={() => toggleGroup(item.key)}
        />
      );
    default:
      return null;
  }
}

/** Each timeline item rendered in a flex row with a left mono HH:MM timestamp column. */
function renderItem(item: TimelineItem, expanded: Set<string>, toggleGroup: (key: string) => void) {
  const content = renderItemContent(item, expanded, toggleGroup);
  if (content === null) return null;
  return (
    <div key={item.key} className="flex gap-2 py-px">
      <span className="flex-none w-[44px] font-mono text-[0.625rem] tabular-nums text-ink-faint leading-[1.8] mt-px select-none">
        {tsHHMM(item.ts)}
      </span>
      <div className="min-w-0 flex-1">{content}</div>
    </div>
  );
}

// ─── iterate footer ──────────────────────────────────────────────────────────

function IterateFooter({
  onIterate,
  iterating = false,
}: {
  onIterate: (feedback: string) => void;
  iterating?: boolean;
}) {
  const [text, setText] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const trimmed = text.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < 3;
  const canSubmit = trimmed.length >= 3 && !iterating;

  return (
    <>
      <div className="border-t border-line bg-surface-1 px-4 py-3 space-y-2">
        <div className="text-xs font-semibold text-ink">Iterate with feedback</div>
        <textarea
          className="input h-20 w-full resize-y font-mono text-xs leading-relaxed"
          placeholder="e.g. The null case in auth.ts is still unhandled — fix it and add a test."
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={iterating}
        />
        {tooShort && (
          <p className="text-xs text-ink-faint">At least 3 characters required.</p>
        )}
        <div className="flex justify-end">
          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={!canSubmit}
            onClick={() => setConfirmOpen(true)}
          >
            {iterating && <Spinner className="h-3.5 w-3.5" />}
            Iterate
          </button>
        </div>
      </div>
      <ConfirmDialog
        open={confirmOpen}
        title="Start a new iteration?"
        confirmLabel="Iterate"
        confirmClassName="btn-primary"
        busy={iterating}
        message={
          <div className="space-y-3">
            <p>Start a new agent run based on this feedback? A child run will be queued and will count toward your usage.</p>
            <div className="rounded border border-line bg-surface-2 px-3 py-2 font-mono text-xs text-ink-muted whitespace-pre-wrap">
              {trimmed}
            </div>
          </div>
        }
        onConfirm={() => {
          onIterate(trimmed);
          setText('');
          setConfirmOpen(false);
        }}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
}

// ─── live bar ────────────────────────────────────────────────────────────────

function liveActivity(lines: TimelineLine[]): string {
  const last = lines[lines.length - 1];
  if (!last) return 'Working…';
  if (last.kind === 'tool_use') {
    const tool = last.meta?.tool ?? 'tool';
    const detail = last.meta?.detail ? ` — ${last.meta.detail}` : '';
    return `Running ${tool}${detail}`;
  }
  if (last.kind === 'prose' || last.kind === 'thinking') return 'Writing…';
  if (last.kind === 'stage') return last.text;
  return 'Working…';
}

function LiveBar({ lines }: { lines: TimelineLine[] }) {
  return (
    <div className="flex items-center justify-between gap-3 border-t border-line bg-surface-2 px-4 py-2 text-xs text-ink-muted">
      <span className="inline-flex items-center gap-1.5">
        <LiveDot />
        {liveActivity(lines)}
      </span>
      <span className="text-ink-faint">{lines.length} entries</span>
    </div>
  );
}

// ─── component ───────────────────────────────────────────────────────────────

export function AgentTimeline({
  lines,
  live,
  height = 'h-[480px]',
  feedback,
  onIterate,
  iterating,
}: {
  lines: TimelineLine[];
  live?: boolean;
  height?: string;
  /** Seeded feedback that triggered this run iteration. Shown as context above the log. */
  feedback?: string | null;
  /** When provided (and not live), shows an inline feedback input to start a new iteration. */
  onIterate?: (feedback: string) => void;
  iterating?: boolean;
}) {
  const [mode, setMode] = useState<'timeline' | 'raw'>('timeline');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  const items = useMemo(() => buildTimeline(lines), [lines]);

  const toggleGroup = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Track whether the user has scrolled up (pauses auto-follow).
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    setAutoScroll(atBottom);
  };

  useLayoutEffect(() => {
    if (!autoScroll) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines, autoScroll]);

  // Re-stick to bottom when re-enabled.
  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [autoScroll]);

  return (
    <div className="overflow-hidden rounded-[8px] border border-line bg-surface-2 shadow-[inset_0_1px_3px_rgba(40,40,35,0.06)]">
      {feedback && (
        <div className="flex items-start gap-3 border-b border-line bg-surface-1 px-4 py-2.5">
          <span className="mt-px shrink-0 font-display text-[0.625rem] font-bold uppercase tracking-[0.08em] text-ink-muted">
            Feedback
          </span>
          <span className="text-xs italic text-ink-muted whitespace-pre-wrap">{feedback}</span>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line bg-surface-2 px-4 py-3">
        <div className="flex items-center gap-2 text-xs text-ink-faint">
          <h2 className="text-sm font-semibold text-ink">Agent output</h2>
          {live && (
            <span className="inline-flex items-center gap-1.5 text-signal">
              <LiveDot />
              live
            </span>
          )}
          <span className="hidden text-ink-faint sm:inline">·</span>
          <span className="hidden sm:inline">{lines.length} lines</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex overflow-hidden rounded-md border border-line">
            <button
              type="button"
              onClick={() => setMode('timeline')}
              className={`px-2.5 py-1 text-xs ${
                mode === 'timeline' ? 'bg-surface-3 text-ink' : 'text-ink-faint hover:text-ink-muted'
              }`}
            >
              Timeline
            </button>
            <button
              type="button"
              onClick={() => setMode('raw')}
              className={`px-2.5 py-1 text-xs ${
                mode === 'raw' ? 'bg-surface-3 text-ink' : 'text-ink-faint hover:text-ink-muted'
              }`}
            >
              Raw
            </button>
          </div>
          {!autoScroll && (
            <button type="button" className="btn-secondary btn-sm" onClick={() => setAutoScroll(true)}>
              Jump to latest
              <ArrowDown className="h-3 w-3" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className={`${height} overflow-y-auto bg-surface-2 px-4 py-3 text-[0.8125rem] leading-relaxed`}
      >
        {lines.length === 0 ? (
          <div className="py-8 text-center text-ink-faint">
            No output yet{live ? ' — waiting for the agent…' : '.'}
          </div>
        ) : mode === 'raw' ? (
          lines.map(renderRawRow)
        ) : (
          items.map((item) => renderItem(item, expanded, toggleGroup))
        )}
      </div>
      {live && <LiveBar lines={lines} />}
      {!live && onIterate && (
        <IterateFooter onIterate={onIterate} iterating={iterating} />
      )}
    </div>
  );
}
